/**
 * #53 — what the Map View draws for one driver's current job: which sites, and which
 * straight route segments. ONE rule, so the console layers (and any later map) cannot
 * disagree about what a line means.
 *
 * The line shows what is LEFT to do, so it shrinks as the load moves:
 *
 *   status                                          stage        segments (both ends known)
 *   EN_ROUTE_TO_PICKUP                              TO_PICKUP    driver→pickup, pickup→disposal
 *   ARRIVED_PICKUP, ONSITE_WORK_START/END           AT_PICKUP    pickup→disposal
 *   EN_ROUTE_TO_DROP_OFF                            TO_DISPOSAL  driver→disposal
 *   DISPOSAL_IN, UNLOADING_START, DISPOSAL_OUT      AT_DISPOSAL  (none; disposal marker only)
 *   anything else (PENDING, PAUSED, COMPLETED and   NONE         nothing at all
 *   later, CANCELLED, REJECTED, unknown, null)
 *
 * NONE matters: GET /v1/drivers/positions attaches the job of the driver's LATEST GPS
 * event, which can be one they already finished. Drawing it would show a route that is
 * over.
 *
 * Missing points are never invented. A segment is drawn only when both ends are known:
 *   - no disposal planned yet, or an ON_SITE job type (no disposal leg by design), or a
 *     destination with no coordinates → the line stops at the pickup;
 *   - a pickup with no coordinates (an unplaced battery, a well with no wellhead) → no
 *     pickup marker, and the driver is joined straight to the disposal instead, so what is
 *     known is still shown.
 *
 * Browser-safe: no imports. Web apps import it via the `@lodex-pro/shared/map-route`
 * subpath, never the barrel (which drags drizzle into a bundle).
 */

export type RouteStage = 'TO_PICKUP' | 'AT_PICKUP' | 'TO_DISPOSAL' | 'AT_DISPOSAL' | 'NONE';

const STAGE_BY_STATUS: Readonly<Record<string, RouteStage>> = {
  EN_ROUTE_TO_PICKUP: 'TO_PICKUP',
  ARRIVED_PICKUP: 'AT_PICKUP',
  ONSITE_WORK_START: 'AT_PICKUP',
  ONSITE_WORK_END: 'AT_PICKUP',
  EN_ROUTE_TO_DROP_OFF: 'TO_DISPOSAL',
  DISPOSAL_IN: 'AT_DISPOSAL',
  UNLOADING_START: 'AT_DISPOSAL',
  DISPOSAL_OUT: 'AT_DISPOSAL',
};

/** The stage a job status puts the driver in. Unknown or null → NONE (draw nothing). */
export function routeStageForStatus(statusCode: string | null | undefined): RouteStage {
  if (statusCode == null) return 'NONE';
  return Object.prototype.hasOwnProperty.call(STAGE_BY_STATUS, statusCode)
    ? (STAGE_BY_STATUS[statusCode] ?? 'NONE')
    : 'NONE';
}

export type RoutePoint = 'DRIVER' | 'PICKUP' | 'DISPOSAL';

export interface RoutePlan {
  readonly stage: RouteStage;
  /** Draw the pickup site marker. */
  readonly showPickup: boolean;
  /** Draw the disposal site marker. */
  readonly showDisposal: boolean;
  /** Straight segments to draw, in travel order. */
  readonly segments: ReadonlyArray<readonly [RoutePoint, RoutePoint]>;
}

/**
 * The plan for one driver. `hasPickup` / `hasDisposal` mean "this point has usable
 * coordinates" — the caller decides that from the payload (both halves present).
 */
export function routePlan(
  statusCode: string | null | undefined,
  hasPickup: boolean,
  hasDisposal: boolean,
): RoutePlan {
  const stage = routeStageForStatus(statusCode);
  const segments: Array<readonly [RoutePoint, RoutePoint]> = [];
  switch (stage) {
    case 'TO_PICKUP':
      if (hasPickup) segments.push(['DRIVER', 'PICKUP']);
      if (hasPickup && hasDisposal) segments.push(['PICKUP', 'DISPOSAL']);
      if (!hasPickup && hasDisposal) segments.push(['DRIVER', 'DISPOSAL']);
      return { stage, showPickup: hasPickup, showDisposal: hasDisposal, segments };
    case 'AT_PICKUP':
      // The driver is at the pickup; without its coordinates the driver stands in for it.
      if (hasDisposal) segments.push([hasPickup ? 'PICKUP' : 'DRIVER', 'DISPOSAL']);
      return { stage, showPickup: hasPickup, showDisposal: hasDisposal, segments };
    case 'TO_DISPOSAL':
      if (hasDisposal) segments.push(['DRIVER', 'DISPOSAL']);
      return { stage, showPickup: false, showDisposal: hasDisposal, segments };
    case 'AT_DISPOSAL':
      return { stage, showPickup: false, showDisposal: hasDisposal, segments };
    case 'NONE':
      return { stage, showPickup: false, showDisposal: false, segments };
  }
}

/** True when both halves of a coordinate pair are present (not a half-placed site). */
export function hasPoint(lat: number | null | undefined, lng: number | null | undefined): boolean {
  return lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng);
}
